import { themeModeAtom } from '@twistopayments/design-system/components/atoms/theme-provider'
import { useAtom } from 'jotai'
import type { AppContext, AppProps } from 'next/app'
import App from 'next/app'
import { DefaultSeo } from 'next-seo'

import {
  Favicons,
  GtmInit,
  MetaTags,
} from '@twisto/components/organisms/head-tags'
import { LiveChatWidget } from '@twisto/components/organisms/live-chat-widget'
import { parseLocale } from '@twisto/nextjs/locale'

import { HomepageApp } from '../components/homepage-app'
import { getGlobalMetadata } from '../utils/api'
import { getRegionFromRequest } from '../utils/region'

import type { DynamicPageProps } from './[[...path]]'

type Props = AppProps & {
  region?: GLOBALS.Region
}

const MyApp = ({ Component, pageProps, router, region }: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { region: localeRegion, lang } = parseLocale(router.locale as any)
  const { global } = pageProps as DynamicPageProps

  const { metaTitleSuffix = '', seo } = global?.data ?? {}

  const [, setThemeMode] = useAtom(themeModeAtom)

  setThemeMode('light')

  return (
    <>
      <DefaultSeo
        title={seo?.metaTitle}
        titleTemplate={`%s${metaTitleSuffix}`}
      />
      <MetaTags lang={lang} region={localeRegion} />
      <Favicons />
      <GtmInit region={localeRegion} />

      <HomepageApp region={region}>
        <Component {...pageProps} />
        <LiveChatWidget />
      </HomepageApp>
    </>
  )
}

// getInitialProps disables automatic static optimization for pages that don't
// have getStaticProps. So [[...url]] pages still get SSG.
// Replace this with getStaticProps once this issue is fixed:
// https://github.com/vercel/next.js/discussions/10949
MyApp.getInitialProps = async (appContext: AppContext) => {
  // Calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext)

  const { data } = await getGlobalMetadata()

  // Only resolvable on the server; `undefined` on the client lets the app
  // config derive the region from `window.location.host`.
  const region = getRegionFromRequest(appContext.ctx.req)

  return {
    ...appProps,
    pageProps: {
      global: data,
    },
    region,
  }
}

export default MyApp
